import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { GoldThread } from '@/components/brand/GoldThread';
import { normalizeBahrainPhone } from '@/lib/format';

const contactSchema = z.object({
  name:    z.string().min(2, 'Please enter your name'),
  phone:   z.string().min(8, 'Please enter your phone'),
  message: z.string().min(5, 'A short message helps us reply').max(2000),
});
type ContactInput = z.infer<typeof contactSchema>;

export default function ContactPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const { register, handleSubmit, formState: { errors }, reset } = useForm<ContactInput>();

  async function onSubmit(values: ContactInput) {
    setStatus('sending');
    try {
      // Phase 6: no contact-form endpoint yet on the backend.
      // Open a mailto with the form contents — works on every device, no
      // backend dependency. When we add a /api/v1/public/contact endpoint
      // in a later phase, swap this for a real fetch().
      const subject = encodeURIComponent(`Website enquiry from ${values.name}`);
      const body = encodeURIComponent(
        `From: ${values.name}\nPhone: ${normalizeBahrainPhone(values.phone)}\n\n${values.message}`,
      );
      window.location.href = `mailto:info@thelaundryco.com?subject=${subject}&body=${body}`;
      setStatus('sent');
      reset();
    } catch {
      setStatus('error');
    }
  }

  return (
    <section className="bg-bone">
      <div className="container pt-20 pb-24 md:pt-32 md:pb-32">
        <div className="grid grid-cols-12 gap-y-12 gap-x-8 md:gap-x-12">
          {/* Left: info */}
          <div className="col-span-12 md:col-span-5 animate-fade-up">
            <div className="flex items-center gap-4 mb-6">
              <GoldThread />
              <span className="eyebrow">{t('home.contact_eyebrow')}</span>
            </div>
            <h1 className="font-display text-d-2 text-ink"
                style={{ fontVariationSettings: "'opsz' 144, 'wght' 500" }}>
              {t('contact.title')}
            </h1>
            <p className="mt-6 max-w-md font-body text-lg text-charcoal leading-relaxed">
              {t('contact.lead')}
            </p>

            <dl className="mt-12 space-y-8">
              <ContactRow icon={<MapPin size={18} className="text-gold" />} label={t('contact.address_label')} value={t('contact.address_value')} />
              <ContactRow icon={<Phone  size={18} className="text-gold" />} label={t('contact.phone_label')}   value={t('contact.phone_value')} href="tel:+97334240115" />
              <ContactRow icon={<Mail   size={18} className="text-gold" />} label={t('contact.email_label')}   value={t('contact.email_value')} href="mailto:info@thelaundryco.com" />
              <ContactRow icon={<Clock  size={18} className="text-gold" />} label={t('contact.hours_label')}
                          value={`${t('contact.hours_weekdays')}\n${t('contact.hours_friday')}`} />
            </dl>
          </div>

          {/* Right: form */}
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <div className="surface p-8 md:p-10 shadow-card">
              <h2 className="font-display text-3xl text-ink mb-8"
                  style={{ fontVariationSettings: "'opsz' 90, 'wght' 500" }}>
                {t('contact.form_title')}
              </h2>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <Field label={t('contact.form_name')} htmlFor="name" error={errors.name?.message}>
                  <Input id="name" placeholder={t('contact.form_name_placeholder')} {...register('name')} error={!!errors.name} />
                </Field>
                <Field label={t('contact.form_phone')} htmlFor="phone" error={errors.phone?.message}>
                  <div className="flex">
                    <span className="inline-flex items-center px-4 h-12 bg-bone-soft border border-line border-e-0 rounded-s-xs font-body text-base text-charcoal">+973</span>
                    <Input id="phone" type="tel" inputMode="tel" className="rounded-s-none" placeholder={t('auth.phone_placeholder')} {...register('phone')} error={!!errors.phone} />
                  </div>
                </Field>
                <Field label={t('contact.form_message')} htmlFor="message" error={errors.message?.message}>
                  <Textarea id="message" rows={5} placeholder={t('contact.form_message_placeholder')} {...register('message')} error={!!errors.message} />
                </Field>

                <Button type="submit" variant="primary" size="lg" loading={status === 'sending'} className="w-full">
                  {status === 'sending' ? t('contact.form_sending') : t('contact.form_submit')}
                  {status !== 'sending' && <Send size={16} />}
                </Button>

                {status === 'sent'  && <p className="text-sm text-success font-body">{t('contact.form_sent')}</p>}
                {status === 'error' && <p className="text-sm text-danger  font-body">{t('contact.form_error')}</p>}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, value, href }: { icon: React.ReactNode; label: string; value: string; href?: string }) {
  return (
    <div className="flex gap-4">
      <span className="mt-1 flex-shrink-0">{icon}</span>
      <div>
        <dt className="eyebrow text-charcoal/60 mb-1">{label}</dt>
        <dd className="font-body text-base text-ink whitespace-pre-line leading-relaxed">
          {href ? <a href={href} className="hover:text-gold-deep">{value}</a> : value}
        </dd>
      </div>
    </div>
  );
}
